import React, { useEffect, useState, useContext } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { FaFile, FaMapMarkerAlt } from "react-icons/fa";
import { DashboardContext } from "../../../../../contexts/Dashboard/DashboardContext";
import HeaderComponent from "../../../../HeaderComponent";
import { Documents } from ".";

type GroupByOption = "documents" | "stops";

type ISeries = {
  name: string;
  data: number[];
}[];

type ChartBarProps = Documents & {
  groupBy: GroupByOption;
  mode?: string;
  onGroupByChange: (next: GroupByOption) => void;
};

export const ChartBar = ({ data, groupBy, mode, onGroupByChange }: ChartBarProps) => {
  const { getSizesComponent, setFontSize, componentProperty, generateRedirectUrl } = useContext(
    DashboardContext
  );
  const [completeRequisition, setCompleteRequisition] = useState(false);
  const [heightChart, setHeightChart] = useState(380);
  const [widthChart, setWidthChart] = useState(300);
  const [categories, setCategories] = useState<string[]>([]);
  const [series, setSeries] = useState<ISeries>([]);

  useEffect(() => {
    data.documents.map((element, key) =>{
      if(element.region === null){
        data.documents[key].region = 'SEM REGIÃO';
      }
    });
    if (data && data.documents) {
        const series: ISeries = [
            {
              name: "Sem Rota",
              data: data.documents.map((element) => Number(element.pending)),
            },
            {
              name: "Em Trânsito",
              data: data.documents.map((element) =>
                Number(element.in_route)
              ),
            },
            {
              name: "Não entregues",
              data: data.documents.map((element) => Number(element.not_delivered)),
            },
            {
              name: "Finalizados APP",
              data: data.documents.map((element) => Number(element.finished) - Number(element.finished_console)),
            },
            {
              name: "Finalizados Console",
              data: data.documents.map((element) =>
                Number(element.finished_console)
              ),
            },
          ];
      
          setSeries(series);
          setCategories(data.documents.map((element) => element.region));
      
    }

    const sizes = getSizesComponent({ id: "chart-adoption-rate-region" });
    if (sizes.width < 345) {
      setFontSize({ id: "chart-adoption-rate-region", width: sizes.width });
    }
    setHeightChart(sizes.height - 40);
    setWidthChart(sizes.width);
    setCompleteRequisition(true);
  }, [data]);

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      stacked: true,
      toolbar: {
        show: false,
      },
      events: {
        dataPointSelection: async (event, chartContext, config) => {
          let selected = categories[config.dataPointIndex];

          if(selected === 'SEM REGIÃO'){
            selected = 'null';
          }
          const type = config.seriesIndex;
          let status: number[] = [];
          let justification = '';

          switch (type) {
            case 0:
              status = [12, 15, 20];
              break;

            case 1:
              status = [0,1,2,5,7,10,11,16,18];
              break;

            case 2:
              status = [6,9];
              break;

            case 3:
              status = [3,4,8,13,17];
              break;

            case 4:
              status = [3,4,8,13,17];
              justification = "Ajuste manual";
              break;

            default:
              break;
          }

          if (
            mode === "view"
            &&
            componentProperty({
              id: "chartAdoptionRateRegion",
              property: "redirect",
            })
          ) {
            window.open(
              await generateRedirectUrl("chartAdoptionRateRegion", {
                redirectTowerToIndex: true,
                date: true,
                eDate: true,
                status_stop_id: status,
                region: selected,
                justification: justification,
              }),
              "_blank"
            );
          }
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        dataLabels: {
          total: {
            enabled: true,
            offsetX: 0,
            style: {
              fontSize: "13px",
              fontWeight: 900,
            },
          },
        },
      },
    },
    stroke: {
      width: 1,
      colors: ["#989898", "#F9AA00", "#E16363", "#5ABF3B", "#419C25"],
    },
    colors: ["#989898", "#F9AA00", "#E16363", "#5ABF3B", "#419C25"],
    xaxis: {
        categories: categories,
        labels: {
          formatter: function (val) {
            return val;
          }
        }
      },
      yaxis: {
        title: {
          text: undefined
        },
      },
    grid: {
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      offsetX: 40,
    },
  };

  const isEditMode = mode === "edit";
  const headerRight = isEditMode ? (
    <div
      className="header-agrupamento-trailing gap-2"
      style={{ fontSize: "1rem" }}
      aria-label="Agrupamento"
      onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
        e.stopPropagation()
      }
    >
      <FaFile
        aria-hidden
        style={{
          opacity: groupBy === "documents" ? 1 : 0.5,
          cursor: "pointer",
        }}
        onClick={() => onGroupByChange("documents")}
      />
      <button
        type="button"
        role="switch"
        aria-checked={groupBy === "stops"}
        aria-label={
          groupBy === "stops" ? "Agrupar por paradas" : "Agrupar por documentos"
        }
        title={groupBy === "stops" ? "Paradas" : "Documentos"}
        onClick={() =>
          onGroupByChange(groupBy === "documents" ? "stops" : "documents")
        }
        style={{
          width: 36,
          height: 20,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.3)",
          background: groupBy === "stops" ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
          cursor: "pointer",
          padding: 0,
          position: "relative",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: groupBy === "documents" ? 2 : 18,
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#fff",
            transition: "left 0.2s ease",
          }}
        />
      </button>
      <FaMapMarkerAlt
        aria-hidden
        style={{
          opacity: groupBy === "stops" ? 1 : 0.5,
          cursor: "pointer",
        }}
        onClick={() => onGroupByChange("stops")}
      />
    </div>
  ) : (
    <span style={{ fontSize: "1rem" }} aria-hidden>
      {groupBy === "documents" ? (
        <FaFile title="Documentos" />
      ) : (
        <FaMapMarkerAlt title="Paradas" />
      )}
    </span>
  );

  return completeRequisition ? (
    <div className="h100">
      <HeaderComponent
        title="Adesão por Região"
        clickable={componentProperty({
          id: "chartAdoptionRateRegion",
          property: "redirect",
        })}
        id="chart-adoption-rate-region"
        rightContent={headerRight}
      />
      <div
        id={
          categories.length <= 10 ||
          getSizesComponent({ id: "chart-adoption-rate-region" }).height >
            categories.length * 183
            ? "fixedBox"
            : "scrollBox"
        }
      >
      <div id="chart">
        <Chart
          type="bar"
          options={options}
          series={series}
          height={heightChart}
          width={widthChart}
        />
      </div>
      </div>
    </div>
  ) : (
    <></>
  );
};
